import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './nested-categories.scss';
import CancelIcon from '@mui/icons-material/Cancel';
import { CategoryTreeItem } from '@zonofi/zono-money-store';
import { RenderCategories } from '../render-categories';

interface NestedCategoriesProps {
  categories: CategoryTreeItem;
  updateSelectedCategories: (val: CategoryTreeItem[]) => void;
}

export const NestedCategories: React.FC<NestedCategoriesProps> = ({
  categories,
  updateSelectedCategories,
}) => {
  const [selectedCategories, setSelectedCategories] = useState<
    CategoryTreeItem[]
  >([]);
  const [activeParent, setActiveParent] = useState<CategoryTreeItem | null>(
    null
  );

  // Handle selecting a category
  const handleCategoryClick = useCallback(
    (category: CategoryTreeItem) => {
      if (category.children?.length) {
        setSelectedCategories([]);
        setActiveParent(category);
        updateSelectedCategories([category]);
      } else {
        setSelectedCategories((prev) => {
          const newCategories = prev.some((c) => c.id === category.id)
            ? prev.filter((c) => c.id !== category.id)
            : [...prev, category];

          if (newCategories.length === 0) {
            updateSelectedCategories(activeParent ? [activeParent] : []);
          }

          return newCategories;
        });
      }
    },
    [activeParent, updateSelectedCategories]
  );

  // Find a parent recursively
  const findParentCategory = useCallback(
    (category: CategoryTreeItem, parentId: string): CategoryTreeItem | null => {
      if (category.id === parentId) return category;
      for (const child of category.children || []) {
        const result = findParentCategory(child, parentId);
        if (result) return result;
      }
      return null;
    },
    []
  );

  const onClickBack = useCallback(() => {
    if (activeParent?.parentId) {
      const parentCategory = findParentCategory(
        categories,
        activeParent.parentId
      );
      if (
        parentCategory &&
        parentCategory.categoryCode !== '__root_category__'
      ) {
        setActiveParent(parentCategory);
        setSelectedCategories([]);
        updateSelectedCategories([parentCategory]);
      } else {
        setActiveParent(null);
        setSelectedCategories([]);
        updateSelectedCategories([]);
      }
    } else {
      setActiveParent(null);
    }
  }, [activeParent, categories, findParentCategory, updateSelectedCategories]);

  // Update external state when selection changes
  useEffect(() => {
    if (selectedCategories.length) {
      updateSelectedCategories(selectedCategories);
    }
  }, [selectedCategories, updateSelectedCategories]);

  // Build parent and child display items like ChipFilter
  const displayItems = useMemo(() => {
    const parentItems: CategoryTreeItem[] = [];
    const childItems: CategoryTreeItem[] = [];

    const traverse = (items: CategoryTreeItem[]) => {
      for (const item of items) {
        if (
          item.children?.length &&
          selectedCategories.some((c) => c.id === item.id)
        ) {
          parentItems.push(item);
          traverse(item.children);
        } else {
          childItems.push(item);
        }
      }
    };

    traverse(activeParent?.children || categories?.children || []);
    return { parentItems, childItems };
  }, [categories?.children, activeParent, selectedCategories]);

  if (!categories) return null;

  return (
    <div className="category-container">
      <div className="category-list">
        {!activeParent ? (
          <>
            <button
              className={`category-button font-medium ${
                selectedCategories.length === 0 ? 'selected' : ''
              }`}
              onClick={() => {
                setSelectedCategories([]);
                updateSelectedCategories([]);
              }}
            >
              All
            </button>
            <RenderCategories
              categories={displayItems.childItems}
              handleCategoryClick={handleCategoryClick}
              selectedCategories={selectedCategories}
            />
          </>
        ) : (
          <div className="active-category">
            <button
              className="parent-category font-medium"
              onClick={onClickBack}
            >
              {activeParent.name}
              <CancelIcon fontSize="small" sx={{ color: '#ffffff' }} />
            </button>
            <div className="child-categories">
              <button
                className={`category-button font-medium ${
                  selectedCategories.length === 0 ? 'selected' : ''
                }`}
                onClick={() => {
                  setSelectedCategories([]);
                  updateSelectedCategories([activeParent]);
                }}
              >
                All
              </button>
              <RenderCategories
                categories={displayItems.childItems}
                handleCategoryClick={handleCategoryClick}
                selectedCategories={selectedCategories}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
